'use client';

import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({ error, errorInfo });

        // Could send to error tracking service here
        // e.g., Sentry.captureException(error);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleGoHome = () => {
        window.location.href = '/dashboard';
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-[400px] flex items-center justify-center p-6">
                    <div className="max-w-md w-full text-center">
                        {/* Icon */}
                        <div className="w-20 h-20 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-10 h-10 text-red-400" />
                        </div>

                        {/* Message */}
                        <h2 className="text-2xl font-playfair font-bold text-white mb-2">
                            Something went wrong
                        </h2>
                        <p className="text-white/60 mb-6">
                            The cosmic energies got a bit tangled. Don&apos;t worry, this happens sometimes.
                        </p>

                        {/* Error details (collapsible) */}
                        {this.state.error && (
                            <details className="mb-6 text-left">
                                <summary className="text-sm text-white/40 cursor-pointer hover:text-white/60 flex items-center gap-2 justify-center">
                                    <Bug size={14} />
                                    Technical details
                                </summary>
                                <pre className="mt-3 p-4 bg-black/30 rounded-xl text-xs text-red-300/80 overflow-x-auto">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                            <button
                                onClick={this.handleRetry}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-400 hover:to-orange-400 transition-all"
                            >
                                <RefreshCw size={18} />
                                Try Again
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all border border-white/10"
                            >
                                <Home size={18} />
                                Go Home
                            </button>
                        </div>

                        {/* Reload hint */}
                        <p className="mt-6 text-xs text-white/30">
                            If the problem persists,{' '}
                            <button onClick={this.handleReload} className="text-amber-400 hover:underline">
                                reload the page
                            </button>
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
